import { useState, useCallback } from 'react';
import { useEngagementData } from '../../../hooks/useEngagementData';
import { useActivityData } from '../../../hooks/useActivityData';
import { useSearchData } from '../../../hooks/useSearchData';
import { useCSVExport } from '../../../hooks/useCSVExport';
import { useToast } from '@/shared/components/Toast';
import { EngagementsBanner } from '../engagements/EngagementsBanner';
import { EngagementTrendChart } from '../engagements/EngagementTrendChart';
import { EngagementBreakdowns } from '../engagements/EngagementBreakdowns';
import { LoginKPICards } from '../activity/LoginKPICards';
import { LoginTrendChart } from '../activity/LoginTrendChart';
import { ActiveUsersBarChart } from '../activity/ActiveUsersBarChart';
import { SearchHeatmap } from '../search/SearchHeatmap';
import { UserTypeToggles } from '../search/UserTypeToggles';
import { ExportButton } from '../shared/ExportButton';
import type { AnalyticsQueryParams } from '../../../types/analytics.types';
import { OverviewSectionSkeleton } from './OverviewSectionSkeleton';
import { OverviewSectionError } from './OverviewSectionError';

type OverviewTabProps = {
  queryParams: AnalyticsQueryParams;
  isActive?: boolean;
};

const DEFAULT_USER_TYPES = ['CARE_SEEKER', 'FINANCIAL_ADVISOR', 'SERVICE_PROVIDER'];

export const OverviewTab = ({ queryParams, isActive = true }: OverviewTabProps) => {
  const [searchUserTypes, setSearchUserTypes] = useState<string[]>(DEFAULT_USER_TYPES);
  const { showToast } = useToast();

  const handleExportError = useCallback(
    (message: string) => {
      showToast(`Export failed: ${message}. Please try again.`, 'error', 5000);
    },
    [showToast],
  );

  const engagement = useEngagementData(queryParams, isActive);
  const activity = useActivityData(queryParams, isActive);
  const search = useSearchData({
    ...queryParams,
    userTypes: searchUserTypes.join(','),
  }, isActive);
  const csvExport = useCSVExport({ onError: handleExportError });

  return (
    <div className="space-y-8 pt-6">
      <OverviewEngagements
        engagement={engagement}
        queryParams={queryParams}
        csvExport={csvExport}
      />
      <OverviewLogins
        activity={activity}
        queryParams={queryParams}
        csvExport={csvExport}
      />
      <OverviewSearch
        search={search}
        searchUserTypes={searchUserTypes}
        onUserTypesChange={setSearchUserTypes}
        queryParams={queryParams}
        csvExport={csvExport}
      />
    </div>
  );
};

type SectionHeaderProps = {
  title: string;
  exportSection?: 'engagements' | 'logins' | 'search';
  csvExport: ReturnType<typeof useCSVExport>;
  queryParams?: AnalyticsQueryParams;
};

const SectionHeader = ({
  title,
  exportSection,
  csvExport,
  queryParams,
}: SectionHeaderProps) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {exportSection && (
      <ExportButton
        onClick={() => csvExport.exportCSV(exportSection, queryParams as Record<string, string>)}
        isLoading={csvExport.isExporting}
      />
    )}
  </div>
);

type EngagementSectionProps = {
  engagement: ReturnType<typeof useEngagementData>;
  queryParams: AnalyticsQueryParams;
  csvExport: ReturnType<typeof useCSVExport>;
};

const OverviewEngagements = ({ engagement, queryParams, csvExport }: EngagementSectionProps) => {
  if (engagement.isLoading) return <OverviewSectionSkeleton title="Engagements" variant="engagements" />;
  if (engagement.isError) {
    return <OverviewSectionError title="Engagements" onRetry={() => engagement.refetch()} />;
  }
  if (!engagement.data) return null;

  const d = engagement.data;
  return (
    <section>
      <SectionHeader title="Engagements" exportSection="engagements" csvExport={csvExport} queryParams={queryParams} />
      <div className="space-y-6">
        <EngagementsBanner
          total={d.total}
          growthLabel={d.growthLabel}
          isGrowthPositive={d.isGrowthPositive}
          previousPeriodTotal={d.previousPeriodTotal}
          uniqueInitiators={d.uniqueInitiators}
          uniqueTargets={d.uniqueTargets}
        />
        <EngagementTrendChart data={d.trend} />
        <EngagementBreakdowns byCategory={d.byCategory} byRegion={d.byRegion} />
      </div>
    </section>
  );
};

type ActivitySectionProps = {
  activity: ReturnType<typeof useActivityData>;
  queryParams: AnalyticsQueryParams;
  csvExport: ReturnType<typeof useCSVExport>;
};

const OverviewLogins = ({ activity, queryParams, csvExport }: ActivitySectionProps) => {
  if (activity.isLoading) return <OverviewSectionSkeleton title="Login Activity" variant="logins" />;
  if (activity.isError) {
    return <OverviewSectionError title="Login Activity" onRetry={() => activity.refetch()} />;
  }
  if (!activity.data) return null;

  return (
    <section>
      <SectionHeader title="Login Activity" exportSection="logins" csvExport={csvExport} queryParams={queryParams} />
      <div className="space-y-6">
        <LoginKPICards kpiCards={activity.data.kpiCards} />
        <LoginTrendChart data={activity.data.trend} />
        <ActiveUsersBarChart
          weeklyData={activity.data.activeUsers}
          dailyData={activity.data.trend}
          dateRange={queryParams.dateRange}
        />
      </div>
    </section>
  );
};

type SearchSectionProps = {
  search: ReturnType<typeof useSearchData>;
  searchUserTypes: string[];
  onUserTypesChange: (types: string[]) => void;
  queryParams: AnalyticsQueryParams;
  csvExport: ReturnType<typeof useCSVExport>;
};

const OverviewSearch = ({
  search,
  searchUserTypes,
  onUserTypesChange,
  queryParams,
  csvExport,
}: SearchSectionProps) => {
  if (search.isLoading) return <OverviewSectionSkeleton title="Search Demand" variant="search" />;
  if (search.isError) {
    return <OverviewSectionError title="Search Demand" onRetry={() => search.refetch()} />;
  }
  if (!search.data) return null;

  return (
    <section>
      <SectionHeader title="Search Demand" exportSection="search" csvExport={csvExport} queryParams={queryParams} />
      <div className="space-y-4">
        <UserTypeToggles activeTypes={searchUserTypes} onChange={onUserTypesChange} />
        <SearchHeatmap
          data={search.data.states}
          totalSearches={search.data.totalSearches}
          activeUserTypes={searchUserTypes}
        />
      </div>
    </section>
  );
};
